import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import ScanConsultantReviewDTO from '@src/adapters/dto/scan/ScaneConsultantReviewDTO';
import ScanProcedureDTO from '@src/adapters/dto/scan/ScanProcedureDTO';
import { useSaveScanReassignLazy } from '@src/adapters/hooks/scans/useSaveScanReassign';
import ConsultantAutoComplete from '@src/components/consultant/ConsutlantAutoComplete';
import ProcedureAutoComplete from '@src/components/procedure/ProcedureAutoComplete';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import RepeatIcon from '@mui/icons-material/Repeat';

interface Props {
  scanId: string;
  currentConsultantId: string | null;
  currentProcedure: ScanProcedureDTO | undefined;
  open: boolean;
  close(): void;
  reassigned(updatedConsultantId: string, scan: ScanConsultantReviewDTO): void;
}

function Reassign({
  scanId,
  currentConsultantId,
  currentProcedure,
  open,
  close,
  reassigned,
}: Props) {
  const [consultantId, setConsultantId] = useState<string | null>(null);
  const [procedureId, setProcedureId] = useState<string | null>(null);
  const { execute: reassign } = useSaveScanReassignLazy();

  useEffect(() => {
    if (currentProcedure) {
      setProcedureId(currentProcedure.id);
    }
  }, [currentProcedure]);

  useEffect(() => {
    setConsultantId(currentConsultantId);
  }, [currentConsultantId]);

  const handleConsultant = (id: string | null) => {
    setConsultantId(id);
  };

  const handleProcedure = (id: string | null) => {
    setProcedureId(id);
  };

  const handleSave = () => {
    if (!consultantId) {
      enqueueSnackbar('A consultant must be selected', { variant: 'error' });
    } else {
      reassign({ scanId: scanId, consultantId, procedureId }).then((result) => {
        reassigned(consultantId, result.scan);
        setProcedureId(null);
        setConsultantId(currentConsultantId);
      });
    }
  };

  return (
    <Dialog open={open} aria-labelledby="Reassign Scan" maxWidth="lg">
      <DialogTitle
        id="alert-dialog-title"
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(2),
        })}>
        <RepeatIcon sx={(theme) => ({ color: theme.palette.primary.main })} />
        Reassign Scan
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: '350px', padding: '20px' }}>
          <ConsultantAutoComplete
            onSelection={(id) => handleConsultant(id)}
            selectedId={consultantId}
            enableNew={true}
          />
        </Box>
        <Box sx={{ width: '350px', padding: '20px' }}>
          <ProcedureAutoComplete
            onSelection={(id) => handleProcedure(id)}
            selectedId={procedureId}
            enableNew={true}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} variant="outlined" color="primary">
          Close
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Reassign;
